import React, { useContext, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import styled from "styled-components";
import { gql } from "apollo-boost";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Link, RouteComponentProps } from "@reach/router";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { Button } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { CurrencyInput } from "../../components/common/CurrencyInput";
import {
  BankTransactionResult,
  MutationConfirmWithdrawCryptoArgs,
  MutationWithdrawCryptoArgs,
} from "../../../../store/generated-models";
import WAValidator from "multicoin-address-validator";
import { AuthContext } from "../../../../core/providers/AuthProvider";
import { LoadWrap, gqlGetSupportedCurrenciesWithRates } from "./Withdraw";
import { round, OERO_PRECISION } from "../../../../core/helpers/Currencies";
import {withTranslation} from "react-i18next";
import {t} from "i18next";

const Row = styled.div`
  display: flex;
`;

const ConfirmWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StyledTextField = styled(TextField)`
  background-color: white;
  .MuiOutlinedInput-root {
    :hover {
      .MuiOutlinedInput-notchedOutline {
        border-color: #9e9e9e;
      }
    }
  }
`;

const Confirm = styled.div`
  border: 1px solid #b5b5b5;
  border-radius: 5px;
  padding: 40px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Alert = styled.span`
  color: ${(props) => props.theme.custom.palette.alert};
  font-weight: bold;
`;

const LinkStyled = styled(Link)`
  text-decoration: none;
  color: ${(props) => props.theme.palette.primary.main};
  &:hover {
    opacity: 0.87;
  }
`;

const gqlWithdrawCrypto = gql`
  mutation withdrawCrypto(
    $immutableAmount: Int!
    $cryptoAddress: String!
    $cryptoCurrency: String!
    $cryptoAmount: Float!
    $oeroAmount: Float!
    $comment: String
  ) {
    withdrawCrypto(
      immutableAmount: $immutableAmount
      cryptoAddress: $cryptoAddress
      cryptoCurrency: $cryptoCurrency
      cryptoAmount: $cryptoAmount
      oeroAmount: $oeroAmount
      comment: $comment
    ) {
      id
      transaction {
        currency
        amount
        comment
      }
    }
  }
`;

const gqlConfirmWithdrawCrypto = gql`
  mutation confirmWithdrawCrypto($id: String) {
    confirmWithdrawCrypto(id: $id) {
      transaction {
        bankTransactionId
        firstUserName
        lastUserName
        companyName
        currency
        amount
        comment
        transactionCode
        transactionNumber
      }
    }
  }
`;

const DEF_CURR_NAME = "BTC";

const initFields = {
  fromAmount: "",
  toAmount: "",
  fromCurrency: "OERO",
  toCurrency: DEF_CURR_NAME,
  address: "",
  comment: "",
};

const defaultCurrency = {
  minAmount: 0,
  name: DEF_CURR_NAME,
  withdrawRate: 0,
};

interface ComponentProps extends RouteComponentProps {}

const Cryptocurrency: React.FC<ComponentProps> = () => {
  const authContext = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const [withdrawCrypto, { loading: withdrawCryptoLoading }] = useMutation<
    { withdrawCrypto: BankTransactionResult },
    MutationWithdrawCryptoArgs
  >(gqlWithdrawCrypto);

  const [confirmWithdrawCrypto, { loading: confirmWithdrawCryptoLoading }] =
    useMutation<
      { confirmWithdrawCrypto: BankTransactionResult },
      MutationConfirmWithdrawCryptoArgs
    >(gqlConfirmWithdrawCrypto);

  const [fields, setFields] = useState<any>({ ...initFields });
  const [errors, setErrors] = useState<any>({});

  const [fromAmountValue, setFromAmountValue] = useState(0);
  const [toAmountValue, setToAmountValue] = useState(0);

  const [currencies, setCurrencies] = useState<any[]>([{ id: DEF_CURR_NAME }]);
  const [immutableAmount, setImmutableAmount] = useState(0);
  const [status, setStatus] = useState("");
  const [txId, setTxId] = useState("");
  const [txInfo, setTxInfo] = useState<any>({});
  const [selectedCurrency, setSelectedCurrency] =
    useState<any>(defaultCurrency);

  const { data, error, loading } = useQuery(gqlGetSupportedCurrenciesWithRates);

  const getSelectedCurrency = (cur: string) => {
    let selCur = data.getSupportedCurrenciesWithRates.crypto.find(
      (el: any) => el.name === cur
    );
    if (!selCur) selCur = defaultCurrency;
    return selCur;
  };

  const setInitial = () => {
    setFields({ ...initFields });
    setStatus("");
    setFromAmountValue(0);
    setToAmountValue(0);
    setImmutableAmount(0);
    setTxId("");
    setTxInfo({});
    setSelectedCurrency(getSelectedCurrency(DEF_CURR_NAME));
  };

  const submitWithdrawCrypto = async () => {
    let err: any = {};
    if (!fromAmountValue) err.fromAmount = "required";
    if (!toAmountValue) err.toAmount = "required";
    if (!fields.address) err.address = "required";

    if (fields.address) {
      const currencyForCheck =
        fields.toCurrency !== "OERO" ? fields.toCurrency : "ETH";
      const addressValid = WAValidator.validate(
        fields.address,
        currencyForCheck === "USDT (ERC-20)" ? "eth" : currencyForCheck
      );
      if (!addressValid) err.address = "invalid address";
    }

    if (
      toAmountValue &&
      fields.toCurrency &&
      authContext.settings.restrictMinAmounts &&
      toAmountValue < selectedCurrency.minAmount
    ) {
      err.toAmount = `minimum ${selectedCurrency.minAmount} ${selectedCurrency.name}`;
    }

    setErrors({ ...err });

    if (Object.keys(err).length !== 0) {
      enqueueSnackbar("Correct errors to continue", { variant: "error" });
      return;
    }

    let errMessage = "";
    try {
      const res = await withdrawCrypto({
        variables: {
          immutableAmount: immutableAmount,
          cryptoAddress: fields.address,
          cryptoCurrency: fields.toCurrency,
          cryptoAmount: toAmountValue,
          oeroAmount: fromAmountValue,
          comment: fields.comment,
        },
      });
      if (res.data.withdrawCrypto.id) {
        setTxId(res.data.withdrawCrypto.id);
        setTxInfo(res.data.withdrawCrypto.transaction);
        setStatus("confirm");
        return;
      }
    } catch (error) {
      try {
        const errorCode = error.graphQLErrors[0].extensions.code;
        if (errorCode === "auth.access_denied") errMessage = "Access denied";
        if (!errMessage && error.graphQLErrors[0].message)
          errMessage = error.graphQLErrors[0].message;
      } catch (ignored) {}
    }

    if (!errMessage) errMessage = "Unknown error";
    enqueueSnackbar(errMessage, { variant: "error" });
  };

  const submitConfirmWithdrawCrypto = async () => {
    let errMessage = "";
    try {
      const res = await confirmWithdrawCrypto({
        variables: {
          id: txId,
        },
      });
      if (res.data.confirmWithdrawCrypto.transaction.transactionCode) {
        setTxInfo(res.data.confirmWithdrawCrypto.transaction);
        setStatus("done");
        return;
      }
    } catch (error) {
      try {
        const errorCode = error.graphQLErrors[0].extensions.code;
        if (errorCode === "core.insufficient_eth") {
          errMessage = "Insufficient ethereum";
        } else if (errorCode === "core.insufficient_tokens") {
          errMessage = "Insufficient tokens";
        }
        if (!errMessage && error.graphQLErrors[0].message)
          errMessage = error.graphQLErrors[0].message;
      } catch (ignored) {}
    }

    if (!errMessage) errMessage = "Unknown error";
    enqueueSnackbar(errMessage, { variant: "error" });
  };

  useEffect(() => {
    if (!data || !data.getSupportedCurrenciesWithRates) return;

    setCurrencies(
      data.getSupportedCurrenciesWithRates.crypto.map((el: any) => {
        return { id: el.name };
      })
    );
    setSelectedCurrency(getSelectedCurrency(DEF_CURR_NAME));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const processValues = (params: any) => {
    if (params.immutable !== undefined) setImmutableAmount(params.immutable);

    let newFields: any = {};
    if (params.fromAmount !== undefined)
      newFields.fromAmount = params.fromAmount;
    if (params.toAmount !== undefined) newFields.toAmount = params.toAmount;
    if (params.toCurrency !== undefined)
      newFields.toCurrency = params.toCurrency;

    let rateVal: number;
    if (params.withdrawRate !== undefined) {
      const selCurr = getSelectedCurrency(params.toCurrency);
      setSelectedCurrency(selCurr);
      rateVal = selCurr.withdrawRate;
    } else {
      rateVal = selectedCurrency.withdrawRate;
    }

    const fromAmountVal =
      params.fromAmount !== undefined ? params.fromAmount : fields.fromAmount;
    const toAmountVal =
      params.toAmount !== undefined ? params.toAmount : fields.toAmount;
    const immutableAmountVal =
      params.immutable !== undefined ? params.immutable : immutableAmount;

    let fromAmt = fromAmountVal === null ? null : parseFloat(fromAmountVal);
    let toAmt = toAmountVal === null ? null : parseFloat(toAmountVal);

    if (fromAmt === null || toAmt === null || !rateVal) {
      setFromAmountValue(0);
      setToAmountValue(0);
      return;
    }

    if (immutableAmountVal === 0) {
      if (isNaN(fromAmt)) {
        setFromAmountValue(0);
        setToAmountValue(0);
        newFields.toAmount = "";
        setFields({ ...fields, ...newFields });
        return;
      }

      fromAmt = round(fromAmt, OERO_PRECISION);
      const calc = round(fromAmt / rateVal, selectedCurrency.precision);
      setFromAmountValue(fromAmt);
      setToAmountValue(calc);
      newFields.toAmount = calc.toString();
      setFields({ ...fields, ...newFields });
    } else {
      if (isNaN(toAmt)) {
        setFromAmountValue(0);
        setToAmountValue(0);
        newFields.fromAmount = "";
        setFields({ ...fields, ...newFields });
        return;
      }

      toAmt = round(toAmt, selectedCurrency.precision);
      const calc = round(toAmt * rateVal, OERO_PRECISION);
      setFromAmountValue(calc);
      setToAmountValue(toAmt);
      newFields.fromAmount = calc.toString();
      setFields({ ...fields, ...newFields });
    }
  };

  const handleInputFromAmountChange = (val: string) => {
    // setFields({...fields, fromAmount: val});
    // setImmutableAmount(0);
    processValues({
      immutable: 0,
      fromAmount: val,
    });
  };

  const handleInputToAmountChange = (val: string) => {
    // setFields({...fields, toAmount: val});
    // setImmutableAmount(1);
    processValues({
      immutable: 1,
      toAmount: val,
    });
  };

  const handleToCurrencyChange = (val: string) => {
    // setRate(getRate(val));
    // setFields({...fields, toCurrency: val});
    // setImmutableAmount(1);
    processValues({
      withdrawRate: true,
      toCurrency: val,
    });
  };

  if (error) {
    return <LoadWrap>Internal server error. Try again later</LoadWrap>;
  }

  if (!data || loading) {
    return (
      <LoadWrap>
        <CircularProgress />
      </LoadWrap>
    );
  }

  // Render CONFIRM step
  if (status === "confirm") {
    return (
      <ConfirmWrap>
        <Confirm>
          <Typography variant={"h2"} style={{ textAlign: "center" }}>
            {t('user.withdraw.please_confirm_transaction')}
          </Typography>

          <div style={{ marginTop: "24px" }}>
            <Typography variant={"h3"}>
              {t('user.withdraw.withdraw_amount')} {txInfo.amount}&nbsp;{txInfo.currency}
            </Typography>
          </div>

          <Row style={{ marginTop: "24px", width: "100%" }}>
            <Button
              variant="contained"
              color="primary"
              size={"large"}
              style={{
                minWidth: "120px",
                maxWidth: "240px",
                width: "100%",
              }}
              onClick={() => submitConfirmWithdrawCrypto()}
              disabled={confirmWithdrawCryptoLoading}
            >
              {t('user.withdraw.confirm')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size={"large"}
              style={{ minWidth: "120px", marginLeft: "20px" }}
              onClick={() => setInitial()}
            >
              {t('user.withdraw.cancel')}
            </Button>
          </Row>
        </Confirm>
      </ConfirmWrap>
    );
  }

  // Render DONE step
  if (status === "done") {
    return (
      <ConfirmWrap>
        <Confirm>
          <Typography variant={"h2"} style={{ textAlign: "center" }}>
            {t('user.withdraw.your_transaction_is')}
          </Typography>
          <Typography variant={"h2"} style={{ textAlign: "center" }}>
            <Alert>{t('user.withdraw.in_processing')}</Alert>
          </Typography>
          <LinkStyled to={"/private/history"} style={{ marginTop: "24px" }}>
            {t('user.withdraw.click_here')}
          </LinkStyled>
        </Confirm>
      </ConfirmWrap>
    );
  }

  // render INITIAL step
  return (
    <>
      <Grid container spacing={3} style={{ maxWidth: "1000px" }}>
        <Grid item xs={12} sm={6}>
          <CurrencyInput
            error={!!errors.fromAmount}
            helperText={errors.fromAmount}
            label={t('user.withdraw.from')}
            amount={fields.fromAmount}
            onAmountChange={(val) => handleInputFromAmountChange(val)}
            currency={fields.fromCurrency}
            onCurrencyChange={(val) =>
              setFields({ ...fields, fromCurrency: val })
            }
            currencies={[{ id: "OERO" }]}
            fullWidth
            dropdownDisabled
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CurrencyInput
            error={!!errors.toAmount}
            helperText={errors.toAmount}
            label={t('user.withdraw.to')}
            amount={fields.toAmount}
            onAmountChange={(val) => handleInputToAmountChange(val)}
            currency={fields.toCurrency}
            onCurrencyChange={(val) => handleToCurrencyChange(val)}
            currencies={currencies}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <StyledTextField
            error={!!errors.address}
            helperText={errors.address}
            variant="outlined"
            fullWidth
            label={t('user.withdraw.address')}
            value={fields.address}
            onChange={(e) => setFields({ ...fields, address: e.target.value })}
          />
          <Typography variant={"body1"} style={{ marginTop: "8px" }}>
            {t('user.withdraw.enter_here')} {fields.toCurrency.toUpperCase()} {t('user.withdraw.enter_here_2')}
          </Typography>
          {/* <Typography variant={"body1"}>
            To identify your payment, we need to know your wallet id, from which
            you will transfer necessary amount.
          </Typography> */}
        </Grid>

        <Grid item xs={12}>
          <StyledTextField
            error={!!errors.comment}
            helperText={errors.comment}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            rowsMax={6}
            label={t('user.withdraw.comment')}
            value={fields.comment}
            //onChange={(event) => setComment(event.target.value)}
            onChange={(e) => setFields({ ...fields, comment: e.target.value })}
          />
        </Grid>
      </Grid>

      <Row style={{ marginTop: "24px" }}>
        <Button
          variant="contained"
          color="primary"
          size={"large"}
          style={{ minWidth: "240px", minHeight: "50px" }}
          onClick={() => submitWithdrawCrypto()}
          disabled={withdrawCryptoLoading}
        >
          {t('user.withdraw.next')}
        </Button>
      </Row>
    </>
  );
};

export default withTranslation()(Cryptocurrency)
